import "../../Resources/Css/common.css";
import "../../Resources/Css/board.css";
import styled from "styled-components";
import { useLocation, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  GetAllNewsData,
  GetData,
  GetPostComment,
  GetSomeData,
} from "../Api/callData";
import { doc, collection, getDoc, deleteDoc } from "firebase/firestore";
import { db, storage } from "../firebase";
import { ref, deleteObject, getMetadata } from "firebase/storage";
import {
  DeleteComment,
  DeleteThisPost,
  ModifyComment,
  SendComment,
} from "../Api/sendData";
import DOMPurify from "dompurify";

/* ── Modern hero styled-components shared with /community pages ─────────── */
const ViewHero = styled.section`
  display: block;
  min-height: auto;
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(48px, 6vw, 72px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;
const ViewHeroInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
`;
const ViewEyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
`;
const ViewTitle = styled.h1`
  font-size: clamp(40px, 5.8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 20px;
`;
const ViewLead = styled.p`
  font-size: clamp(16px, 1.4vw, 18px);
  line-height: 1.6;
  color: #64748b;
  margin: 0 auto 36px;
  max-width: 620px;
`;
const ViewTabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
`;
const ViewTab = styled.button`
  padding: 9px 22px;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 500;
  border: 0;
  cursor: pointer;
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "transparent"};
  box-shadow: ${(p) =>
    p.$active ? "0 6px 14px -6px rgba(99, 102, 241, 0.55)" : "none"};
  transition: color 0.15s ease;
  font-family: inherit;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
`;

/* ── Modern post footer (admin actions + prev/next nav + back-to-list) ─── */
const PostFooter = styled.section`
  max-width: 1100px;
  margin: 64px auto 0;
  padding: 0 24px 96px;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const AdminActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 32px;

  a {
    text-decoration: none;
  }
`;

const HeaderShare = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 18px;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const ShareBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;

  svg {
    color: currentColor;
  }

  &.linkedin:hover {
    color: #ffffff;
    background: #0a66c2;
    border-color: #0a66c2;
  }
  &.x:hover {
    color: #ffffff;
    background: #0f172a;
    border-color: #0f172a;
  }
`;

const ModifyBtn = styled.button`
  padding: 10px 22px;
  border-radius: 999px;
  border: 0;
  background: #0f172a;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease;

  &:hover {
    background: #1e293b;
  }
`;

const DeleteBtn = styled.button`
  padding: 10px 22px;
  border-radius: 999px;
  border: 1px solid #fecaca;
  background: #ffffff;
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #fef2f2;
    border-color: #fca5a5;
  }
`;

const NavList = styled.div`
  border-top: 1px solid #e2e8f0;
`;

const navRowStyles = `
  display: grid;
  grid-template-columns: 110px 1fr auto;
  align-items: center;
  gap: 24px;
  padding: 22px 8px;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 18px 4px;
  }
`;

const NavRow = styled.a`
  ${navRowStyles}
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease;

  &:hover {
    background: #f8fafc;
  }

  .label {
    font-size: 12.5px;
    font-weight: 600;
    color: #94a3b8;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .title {
    font-size: 15.5px;
    color: #0f172a;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 13px;
    color: #94a3b8;
  }

  .arrow {
    color: #cbd5e1;
    transition: color 0.15s ease, transform 0.15s ease;
  }

  &:hover .arrow {
    color: #6366f1;
    transform: translateX(2px);
  }

  @media (max-width: 600px) {
    .title {
      white-space: normal;
      line-height: 1.45;
    }
    .meta {
      font-size: 12.5px;
      justify-self: start;
    }
  }
`;

const NavRowEmpty = styled.div`
  ${navRowStyles}

  .label {
    font-size: 12.5px;
    font-weight: 600;
    color: #cbd5e1;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .empty {
    font-size: 14px;
    color: #cbd5e1;
  }
`;

const BackToListWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

const BackToList = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
`;

const formatBoardDate = (ts) =>
  ts
    .toDate()
    .toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/\./g, "")
    .replace(/\s+/g, "-");

const ArrowRightIcon = () => (
  <svg
    className="arrow"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const ListIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const LinkedInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 11-.01-4.12 2.06 2.06 0 01.01 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.8 0 0 .78 0 1.74v20.52C0 23.22.8 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.74V1.74C24 .78 23.2 0 22.22 0z" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
  </svg>
);

const HERO_META = {
  inform: {
    eyebrow: "Community",
    title: "Inform",
    lead: "Notices, updates, and product announcements from Youngand.",
  },
  news: {
    eyebrow: "Community",
    title: "News",
    lead: "What's happening at Youngand — press coverage, milestones, and announcements.",
  },
  hiring: {
    eyebrow: "Community",
    title: "Recruitment",
    lead: "Join YoungAnd's journey — we're hiring.",
  },
  inquiry: {
    eyebrow: "Support",
    title: "Inquiry",
    lead: "Get in touch with our team.",
  },
  popup: {
    eyebrow: "Admin",
    title: "Popup",
    lead: "",
  },
};

export function BoardView() {
  const location = useLocation();
  const currnetPath = location.pathname;
  const pathWithoutId = currnetPath.replace(/\/[^/]+$/, "");

  // 비밀글인 경우 확인
  const [showContent, setShowContent] = useState(false);
  const [secretInput, setSecretInput] = useState("");
  const [secret, setSecret] = useState("");

  const nowDate = new Date().toLocaleDateString;

  const none = { display: "none" };
  const block = { display: "block" };
  const black = { color: "#000" };

  // 게시물 id값 확인

  const { id } = useParams();
  const numericId = parseInt(id.replace(/\D/g, ""), 10);
  const textPart = id.replace(/\d+/g, "");

  // 수정하기 링크

  const modify =
    localStorage.length === 0
      ? "/boardModify/:" + id
      : "/boardModifyAdmin/:" + id;

  // 받아온 데이터 담기
  const [post, setPost] = useState(null);
  const [prevPost, setPrevPost] = useState(null);
  const [nextPost, setNextPost] = useState(null);
  const [docLength, setDocLength] = useState(0);
  const [thumbnailName, setThumbnailName] = useState([]);
  const [time, setTime] = useState(0);
  const [receiveComment, setReceiveComment] = useState("");
  const [lastVisible, setLastVisible] = useState(null);
  const [totalComment, setTotalComment] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // content 담기
  const [content, setContent] = useState(null);
  const [prev, setPrev] = useState(null);
  const [next, setNext] = useState(null);
  const [comment, setComment] = useState("");

  // 게시물 내용 담기
  const postContent = post ? DOMPurify.sanitize(post.content) : "";

  // table 확인
  useEffect(() => {
    const fetchData = async () => {
      const idPost = await GetData(textPart, id);
      setPost(idPost);
      setSecret(idPost.secret);
      // console.log("현재 데이터 : ", idPost);
    };
    fetchData();
  }, []);

  // 비밀글인 경우 체크
  const handleSecretChange = (e) => {
    setSecretInput(e.target.value);
  };

  const handleSubmitSecret = () => {
    if (secretInput == secret) {
      setShowContent(true);
    } else {
      window.alert("Password is incorrect.");
      window.location.href = "/support/inquiry";
    }
  };

  // 현재 게시물 앞 뒤 정보 가져오기
  useEffect(() => {
    const fetchData = async () => {
      if (time != 0) {
        const { prevPost, nextPost } = await GetSomeData(textPart, time);
        setPrev(prevPost);
        setNext(nextPost);
      }
    };
    fetchData();
  }, [time]);

  const handleComment = (event) => setComment(event.target.value);

  // 댓글 등록
  async function handleCommentSubmit() {
    const commentItem = {
      inq_post: id,
      comment: comment,
      userName: localStorage.length !== 0 ? "YoungAnd" : "",
      reg_time: new Date(),
    };

    console.log("입력한 댓글: ", commentItem);

    const commentResult = await SendComment(textPart, id, commentItem);
    console.log(commentResult);
    if (commentResult == true) {
      console.log("댓글등록 성공");
      window.location.reload();
    } else {
      console.log("댓글등록 실패");
    }
  }

  // 댓글 불러오기
  const loadComment = async (page) => {
    setLoading(true);
    console.log(lastVisible);
    const { postData, lastVisibleDoc, querySnapShotAllSize } =
      await GetPostComment(textPart, id, page, lastVisible);

    console.log(postData);

    setReceiveComment(postData);
    setLastVisible(lastVisibleDoc);
    setLoading(false);
    setTotalComment(querySnapShotAllSize);
  };

  // 처음 렌더링 시 데이터 가져오기
  useEffect(() => {
    if (textPart == "inquiry") {
      // console.log("페이지 호출", lastVisible);
      loadComment(currentPage);
    }
  }, [currentPage]);

  // useEffect(() => {
  //   console.log(post);
  //   console.log(lastVisible);
  //   console.log(totalComment);
  // }, [post, lastVisible, totalComment]);

  // 페이지 번호 변경 시 데이터 재로딩
  const handlePageChange = (page) => {
    if (page < 1 || page > Math.ceil(totalComment / 5)) return;
    setCurrentPage(page);
  };

  // 페이지 번호 생성 (전체 페이지 수에 맞춰서)
  const totalPages = Math.ceil(totalComment / 5);

  useEffect(() => {
    if (showContent && post) {
      const settingPost = document.querySelector(".board_content");
      if (!settingPost) {
        settingPost.innerHTML = post.content;
      }
    }

    if (post) {
      setTime(post.reg_time);
    }
  }, [showContent, post]);

  const jCenter = { justifyContent: "center" };

  // 댓글 수정
  const [editingCommentId, setEditingCommentId] = useState(null); // 수정할 댓글의 ID를 저장
  const [editedComment, setEditedComment] = useState(""); // 수정된 댓글 내용

  const handleEditClick = (commentId, currentComment) => {
    setEditingCommentId(commentId); // 수정하려는 댓글의 ID를 저장
    setEditedComment(currentComment); // 수정하려는 댓글의 현재 내용을 저장
    // console.log(editingCommentId);
    // console.log(editedComment);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null); // 수정 상태 취소
    setEditedComment(""); // 수정된 내용 초기화
  };

  const handleSaveEdit = async () => {
    // 수정된 댓글 저장 로직
    const commentItem = {
      inq_post: id,
      comment: editedComment,
      reg_time: new Date(),
    };
    console.log(commentItem);

    // 댓글 수정 API 호출
    const commentResult = await ModifyComment(
      textPart,
      id,
      editingCommentId,
      commentItem
    );
    if (commentResult === true) {
      console.log("댓글 수정 성공");
      // 수정 후 상태 초기화
      setEditingCommentId(null);
      setEditedComment("");
      window.location.reload();
    } else {
      console.log("댓글 수정 실패");
    }
  };
  // 댓글 삭제
  const handleDelClick = async (commentId) => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      const commentResult = await DeleteComment(textPart, id, commentId);
      if (commentResult === true) {
        window.alert("Comment has been deleted.");
        console.log("댓글 삭제 성공");
        window.location.reload();
      } else {
        console.log("댓글 삭제 실패");
      }
    }
  };

  return (
    <>
      {loading ? (
        <section className="board_section loading_section">
          <div className="spinner-container">
            <img className="spinner" src="/images/spinner.gif" />
          </div>
        </section>
      ) : post &&
        (post.secret === undefined ||
          post.secret === null ||
          post.secret === "" ||
          showContent ||
          localStorage.length > 0) ? (
        <>
          <ViewHero>
            <ViewHeroInner>
              <ViewEyebrow>
                {(HERO_META[textPart] || HERO_META.news).eyebrow}
              </ViewEyebrow>
              <ViewTitle>
                {(HERO_META[textPart] || HERO_META.news).title}
              </ViewTitle>
              {(HERO_META[textPart] || HERO_META.news).lead && (
                <ViewLead>
                  {(HERO_META[textPart] || HERO_META.news).lead}
                </ViewLead>
              )}
              {textPart === "popup" ? (
                <ViewTabs>
                  <ViewTab
                    type="button"
                    $active
                    onClick={() => (window.location.href = "/popup")}
                  >
                    Popup
                  </ViewTab>
                </ViewTabs>
              ) : textPart === "inquiry" ? (
                <ViewTabs>
                  <ViewTab
                    type="button"
                    onClick={() => (window.location.href = "/support/faq")}
                  >
                    FAQ
                  </ViewTab>
                  <ViewTab
                    type="button"
                    $active
                    onClick={() => (window.location.href = "/support/inquiry")}
                  >
                    Inquiry
                  </ViewTab>
                </ViewTabs>
              ) : (
                <ViewTabs>
                  <ViewTab
                    type="button"
                    $active={textPart === "inform"}
                    onClick={() => (window.location.href = "/community/inform")}
                  >
                    Inform
                  </ViewTab>
                  <ViewTab
                    type="button"
                    $active={textPart === "news"}
                    onClick={() => (window.location.href = "/community/news")}
                  >
                    News
                  </ViewTab>
                  <ViewTab
                    type="button"
                    $active={textPart === "hiring"}
                    onClick={() => (window.location.href = "/community/hiring")}
                  >
                    Recruitment
                  </ViewTab>
                </ViewTabs>
              )}
            </ViewHeroInner>
          </ViewHero>
          <div className="container board board_header">
            <div className="board_title">
              {post ? post.title : ""}
              <div className="board_info">
                <div>
                  <span>Date &nbsp;</span>
                  <span className="board_date">
                    {post
                      ? post.reg_time
                          .toDate()
                          .toLocaleDateString("ko-KR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                          .replace(/\./g, "")
                          .replace(/\s+/g, "-")
                      : new Date()
                          .toLocaleDateString("ko-KR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                          .replace(/\./g, "")
                          .replace(/\s+/g, "-")}
                  </span>
                </div>
              </div>
              <HeaderShare>
                <ShareBtn
                  type="button"
                  className="linkedin"
                  onClick={() => {
                    const u = encodeURIComponent(window.location.href);
                    window.open(
                      `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
                      "share-linkedin",
                      "width=600,height=600,noopener,noreferrer"
                    );
                  }}
                  aria-label="Share on LinkedIn"
                >
                  <LinkedInIcon />
                  Share
                </ShareBtn>
                <ShareBtn
                  type="button"
                  className="x"
                  onClick={() => {
                    const u = encodeURIComponent(window.location.href);
                    const t = encodeURIComponent(post?.title || "");
                    window.open(
                      `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
                      "share-x",
                      "width=600,height=600,noopener,noreferrer"
                    );
                  }}
                  aria-label="Share on X"
                >
                  <XIcon />
                  Post
                </ShareBtn>
              </HeaderShare>
            </div>
          </div>

          {/* Main Content Section */}
          <div className="main-contents" style={jCenter}>
            <div className="main-full board">
              <div
                className="board_content"
                dangerouslySetInnerHTML={{ __html: postContent }}
              />
              {/* {post ? post.content : "잠시만 기다려주세요."} */}
              {textPart && textPart == "inquiry" && (
                <div className="comment_board">
                  <div className="comment_input_area">
                    <textarea onChange={handleComment}></textarea>
                    <button
                      className="comment_submit"
                      onClick={handleCommentSubmit}
                    >
                      Input
                    </button>
                  </div>
                  <div className="comment_show_area">
                    {receiveComment
                      ? receiveComment.map((com) => (
                          <div className="comment_box">
                            <div>
                              <p>
                                {com.commentData.userName !== ""
                                  ? com.commentData.userName
                                  : post.userName}
                              </p>
                            </div>
                            <div>
                              {editingCommentId == com.id ? (
                                <input
                                  type="text"
                                  value={editedComment}
                                  onChange={(e) =>
                                    setEditedComment(e.target.value)
                                  }
                                  className="changeInput"
                                />
                              ) : (
                                <p>{com.commentData.comment}</p>
                              )}
                            </div>
                            <div>
                              <p>
                                {com.commentData.reg_time
                                  .toDate()
                                  .toLocaleDateString("ko-KR", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  })}
                              </p>
                            </div>
                            {editingCommentId == com.id ? (
                              <>
                                <input
                                  type="button"
                                  className="delBtn cancelBtn"
                                  onClick={handleCancelEdit}
                                  value="cancel"
                                />
                                <input
                                  type="button"
                                  className="subBtn changeBtn"
                                  onClick={handleSaveEdit}
                                  value="submit"
                                />
                              </>
                            ) : (
                              <>
                                <input
                                  type="button"
                                  id="boardModBtn"
                                  className="subBtn"
                                  value="modify"
                                  onClick={() =>
                                    handleEditClick(
                                      com.id,
                                      com.commentData.comment
                                    )
                                  }
                                />
                                <input
                                  type="button"
                                  id="boardDelBtn"
                                  className="delBtn"
                                  value="delete"
                                  onClick={() => handleDelClick(com.id)}
                                />
                              </>
                            )}
                          </div>
                        ))
                      : ""}
                  </div>
                  {totalPages == 0 ? (
                    ""
                  ) : (
                    <div id="pagenation_area">
                      <ul className="pagenation">
                        <li
                          className={`page-item ${
                            currentPage == 1 || loading ? "disabled" : ""
                          }`}
                        >
                          <a
                            className="page-link"
                            href="#"
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage == 1 || loading}
                          >
                            <i className="fa-solid fa-angles-left"></i>
                          </a>
                        </li>
                        <li
                          className={`page-item ${
                            currentPage == 1 || loading ? "disabled" : ""
                          }`}
                        >
                          <a
                            className="page-link"
                            href="#"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage == 1 || loading}
                          >
                            <i className="fa-solid fa-chevron-left"></i>
                          </a>
                        </li>
                        {totalPages > 0 &&
                          [...Array(totalPages)].map((_, index) => (
                            <li
                              key={index}
                              className={`page-item ${
                                currentPage === index + 1 ? "active" : ""
                              }`}
                            >
                              <a
                                className="page-link"
                                href="#"
                                onClick={() => handlePageChange(index + 1)}
                              >
                                {index + 1}
                              </a>
                            </li>
                          ))}

                        <li
                          className={`page-item ${
                            currentPage == totalPages || loading
                              ? "disabled"
                              : ""
                          }`}
                        >
                          <a
                            className="page-link"
                            href="#"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage == totalPages || loading}
                          >
                            <i className="fa-solid fa-chevron-right"></i>
                          </a>
                        </li>
                        <li
                          className={`page-item ${
                            currentPage == totalPages || loading
                              ? "disabled"
                              : ""
                          }`}
                        >
                          <a
                            className="page-link"
                            href="#"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage == totalPages || loading}
                          >
                            <i className="fa-solid fa-angles-right"></i>
                          </a>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <PostFooter>
            {(post.secret_check || localStorage.length > 0) && (
              <AdminActions>
                <a href={modify}>
                  <ModifyBtn type="button">modify</ModifyBtn>
                </a>
                <DeleteBtn
                  type="button"
                  onClick={() => DeleteThisPost(textPart, id)}
                >
                  delete
                </DeleteBtn>
              </AdminActions>
            )}
            <NavList>
              {prev ? (
                <NavRow
                  href={`${pathWithoutId}/${textPart + (numericId - 1)}`}
                >
                  <span className="label">Prev post</span>
                  <span className="title">{prev.title}</span>
                  <span className="meta">
                    {formatBoardDate(prev.reg_time)}
                    <ArrowRightIcon />
                  </span>
                </NavRow>
              ) : (
                <NavRowEmpty>
                  <span className="label">Prev post</span>
                  <span className="empty">There is no Prev post.</span>
                </NavRowEmpty>
              )}
              {next ? (
                <NavRow
                  href={`${pathWithoutId}/${textPart + (numericId + 1)}`}
                >
                  <span className="label">Next post</span>
                  <span className="title">{next.title}</span>
                  <span className="meta">
                    {formatBoardDate(next.reg_time)}
                    <ArrowRightIcon />
                  </span>
                </NavRow>
              ) : (
                <NavRowEmpty>
                  <span className="label">Next post</span>
                  <span className="empty">There is no Next post.</span>
                </NavRowEmpty>
              )}
            </NavList>
            <BackToListWrap>
              <BackToList href={pathWithoutId}>
                <ListIcon />
                Back to list
              </BackToList>
            </BackToListWrap>
          </PostFooter>
        </>
      ) : textPart !== "inquiry" ? (
        <section className="board_section loading_section">
          <div className="spinner-container">
            <img className="spinner" src="/images/spinner.gif" />
          </div>
        </section>
      ) : (
        <>
          <section className="subpage_header secret_check">
            <div className="breadcrumb">
              <ul>
                <li>
                  <a href="/">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="18px"
                      viewBox="0 -960 960 960"
                      width="18px"
                      fill="#C9C9C9"
                    >
                      <path d="M240-200h147.69v-235.38h184.62V-200H720v-360L480-741.54 240-560v360Zm-40 40v-420l280-211.54L760-580v420H532.31v-235.38H427.69V-160H200Zm280-310.77Z" />
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="#">Support</a>
                </li>
                <li id="bc-inquiry" style={block}>
                  <a style={black} href="/support/inquiry">
                    Inquiry
                  </a>
                </li>
              </ul>
            </div>
          </section>
          <section className="pwCheck_section">
            <div className="check_box">
              <h2>Password Check</h2>
              <strong>
                This post is private.
                <br />
                Please enter your password.
              </strong>
              <div className="input_wrapper">
                <input
                  className="input_pw"
                  type="password"
                  placeholder="Please enter your password."
                  value={secretInput}
                  onChange={handleSecretChange}
                />
                <button className="submit_pw" onClick={handleSubmitSecret}>
                  submit
                </button>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}
